import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { claimMyReferral } from "@lib/apiClients";
import { queryKeys } from "@lib/queryClient";
import { useAuthStore } from "@stores/useAuthStore";
import { useShallow } from "zustand/react/shallow";

const STORAGE_KEY = "bbb_pending_referral_code";

export function readReferralCodeFromLocation(): {
  code: string | null;
  nextUrl: string | null;
} {
  if (typeof window === "undefined") {
    return { code: null, nextUrl: null };
  }

  const url = new URL(window.location.href);
  let code = url.searchParams.get("ref") ?? url.searchParams.get("referralCode");
  let nextPathname = url.pathname;

  const inviteMatch = url.pathname.match(/^\/invite\/([A-Za-z0-9]+)\/?$/);
  if (!code && inviteMatch?.[1]) {
    code = inviteMatch[1];
    nextPathname = "/";
  }

  if (!code) {
    return { code: null, nextUrl: null };
  }

  url.searchParams.delete("ref");
  url.searchParams.delete("referralCode");
  const nextUrl = `${nextPathname}${url.search ? url.search : ""}${url.hash}`;

  return {
    code: code.trim().toUpperCase(),
    nextUrl,
  };
}

export function useHandleReferralToken(options?: { onAuthRequired?: () => void }) {
  const { isAuthenticated, isAuthReady, getSessionAuthToken, refreshUserProfile } =
    useAuthStore(
      useShallow((s) => ({
        isAuthenticated: s.isAuthenticated,
        isAuthReady: s.isAuthReady,
        getSessionAuthToken: s.getSessionAuthToken,
        refreshUserProfile: s.refreshUserProfile,
      })),
    );
  const queryClient = useQueryClient();
  const claimingRef = useRef(false);
  const onAuthRequired = options?.onAuthRequired;

  useEffect(() => {
    const { code, nextUrl } = readReferralCodeFromLocation();
    if (!code || typeof window === "undefined") {
      return;
    }

    localStorage.setItem(STORAGE_KEY, code);
    if (nextUrl) {
      window.history.replaceState({}, "", nextUrl);
    }

    if (!isAuthenticated && onAuthRequired) {
      toast.info("Sign up or log in to register your referral");
      onAuthRequired();
    }
  }, [isAuthenticated, onAuthRequired]);

  useEffect(() => {
    const tryClaim = async () => {
      if (!isAuthenticated || !isAuthReady || claimingRef.current) {
        return;
      }

      if (typeof window === "undefined") {
        return;
      }

      const code = localStorage.getItem(STORAGE_KEY);
      if (!code) {
        return;
      }

      claimingRef.current = true;

      try {
        const token = await getSessionAuthToken();
        const result = await claimMyReferral(token, { code });
        localStorage.removeItem(STORAGE_KEY);
        toast.success(`Referral registered from ${result.referrerDisplayName}`);
        await Promise.all([
          refreshUserProfile(),
          queryClient.invalidateQueries({
            queryKey: queryKeys.myReferralProgram(result.referrerUserId),
          }),
          queryClient.invalidateQueries({
            queryKey: queryKeys.myReferralProgram(
              useAuthStore.getState().authUser?.uid ?? "missing-user",
            ),
          }),
        ]);
      } catch (error) {
        localStorage.removeItem(STORAGE_KEY);
        const message = (error as Error)?.message ?? "Failed to claim referral";
        const lowered = message.toLowerCase();

        if (lowered.includes("already claimed") || lowered.includes("already registered")) {
          toast.info("Referral already registered on this account.");
        } else if (lowered.includes("own referral")) {
          toast.error("You cannot claim your own referral link.");
        } else if (lowered.includes("first approved quest")) {
          toast.error("Referral links must be registered before your first approved quest.");
        } else if (lowered.includes("not found")) {
          toast.error("That referral link is invalid.");
        } else {
          toast.error(message);
        }
      } finally {
        claimingRef.current = false;
      }
    };

    void tryClaim();
  }, [getSessionAuthToken, isAuthenticated, isAuthReady, queryClient, refreshUserProfile]);
}
